import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Target } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DateField from "@/components/forms/DateField";
import api from "@/lib/api";
import { toISODate } from "@/lib/dates";
import { errorMessage } from "@/lib/errors";

/**
 * Acquisition funnel by channel (T22).
 *
 * One row per (source, medium, campaign) with how far the people who arrived
 * through it got: registered, phone verified (OTP), profile complete, first
 * quote sent. The accounts are those created in the chosen range; a later
 * stage counts whenever it happened. No charts, by brief.
 *
 * The CSV is requested from the same endpoint with `format=csv`, through axios
 * so the session cookie travels — a plain link would work only where the API
 * shares the page's origin, and here it does not.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// The API (and the CSV) name the row of accounts with no source in English;
// the screen shows it in the admin's language.
const ORGANIC_ROW = "(direct/organic)";

const COLUMNS = [
  { key: "source", label: "adminAcquisition.source", numeric: false },
  { key: "medium", label: "adminAcquisition.medium", numeric: false },
  { key: "campaign", label: "adminAcquisition.campaign", numeric: false },
  { key: "registered", label: "adminAcquisition.registered", numeric: true },
  { key: "phone_verified", label: "adminAcquisition.otp", numeric: true },
  { key: "profile_completed", label: "adminAcquisition.profile", numeric: true },
  { key: "first_quote", label: "adminAcquisition.firstQuote", numeric: true },
];

export default function AdminAcquisition() {
  const { t } = useTranslation();
  // Last 30 days by default. The calendar day is the person's own, the query is
  // read in UTC — a few hours' difference at the edges of a range is accepted.
  const [from, setFrom] = useState(() => toISODate(new Date(Date.now() - 30 * DAY_MS)));
  const [to, setTo] = useState(() => toISODate(new Date()));
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [from, to]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/acquisition-report", { params: params() });
      setRows(data.rows || []);
      setError("");
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    }
  }, [params, t]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/acquisition-report", {
        params: { ...params(), format: "csv" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `acquisition-${toISODate(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setError("");
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-acquisition">
        <div className="flex items-center gap-3 mb-6">
          <Target size={22} className="text-hive-ink" />
          <h1 className="font-display text-3xl font-bold text-worker">
            {t("adminAcquisition.title")}
          </h1>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="label-base" htmlFor="acq-from">{t("common.from")}</label>
            <DateField id="acq-from" value={from} onChange={setFrom} data-testid="acq-from" />
          </div>
          <div>
            <label className="label-base" htmlFor="acq-to">{t("common.to")}</label>
            <DateField id="acq-to" value={to} onChange={setTo} data-testid="acq-to" />
          </div>
          <button
            type="button" className="fx-btn fx-btn--secondary flex items-center gap-2"
            onClick={downloadCsv} disabled={busy} data-testid="acq-csv"
          >
            <Download size={16} aria-hidden="true" />
            {t("adminAcquisition.downloadCsv")}
          </button>
        </div>

        {error && (
          <p className="text-danger text-sm mb-4" data-testid="acq-error">{error}</p>
        )}

        {/* The wrapper scrolls, never the page: seven columns do not fit a
            phone, and `min-w-0` lets the card shrink inside DashboardLayout. */}
        <div className="card-base min-w-0 overflow-x-auto" data-testid="acq-table-wrap">
          {rows.length === 0 ? (
            <p className="p-6 text-smoke text-sm" data-testid="acq-empty">
              {t("common.noResults")}
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="acq-table">
              <thead>
                <tr className="text-left text-smoke border-b border-sand">
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key} scope="col"
                      className={`p-3 font-semibold whitespace-nowrap ${c.numeric ? "text-right" : ""}`}
                    >
                      {t(c.label)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.source}|${row.medium}|${row.campaign}`}
                    className="border-t border-sand first:border-0"
                    data-testid="acq-row"
                  >
                    {COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className={`p-3 ${c.numeric ? "text-right font-mono" : "break-words"}`}
                      >
                        {c.key === "source" && row.source === ORGANIC_ROW
                          ? t("adminAcquisition.direct")
                          : row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
